import { createReducer } from '@reduxjs/toolkit'
import {
  addDrawingFile,
  addFileDescription,
  replacePage,
  selectPage,
  setIsLoading,
  sortPages,
  switchPageCheckbox,
  updateDrawingFile,
  updatePage,
  updateS3FolderName,
  clearState,
  addPage,
  selectFile,
  clearAnalyzeResults,
  removePage,
  removeDrawingFile,
  setFilesType,
  setConvertIssues,
  setConvertFixes,
  clearConvertResults,
  setConvertFailedError,
} from '../actions/files'
import {
  AnalyzedPage,
  DrawingFile,
  DrawingType,
  AnalyzeResult,
  FileType,
  ConvertFailedErrorType,
} from '../models/File'
import { ConvertFailedResponse, ConvertOverrides } from '../models/Construction'

interface FilesState {
  files: DrawingFile[]
  filesType: FileType | null
  recognizedPages: AnalyzedPage[]
  unrecognizedPages: {
    fileName: string
    pages: AnalyzedPage[]
  }[]
  selectedFileName: string
  selectedPageId: string
  selecetedS3Folder?: string
  isLoading: boolean
  analyzeResult?: AnalyzeResult
  convertResult?: {
    issues: ConvertFailedResponse
    fixes?: ConvertOverrides
  }
  convertFailedError?: {
    type: ConvertFailedErrorType
  }
}

const initialState: FilesState = {
  files: [],
  filesType: null,
  recognizedPages: [],
  unrecognizedPages: [],
  selectedFileName: '',
  selectedPageId: '',
  isLoading: false,
}

const pageNames = ['First', 'Second', 'Third', 'Fourth']

const filesReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(addDrawingFile, (state, action) => {
      state.files.push(action.payload.file)
    })
    .addCase(updateDrawingFile, (state, action) => {
      const fileIndex = state.files.findIndex(
        (file) => file.id === action.payload.fileId,
      )
      if (fileIndex !== -1) {
        state.files[fileIndex] = {
          ...state.files[fileIndex],
          ...action.payload.newFile,
        }
      }
    })
    .addCase(removeDrawingFile, (state, action) => {
      const fileIndex = state.files.findIndex(
        (file) => file.id === action.payload.fileId,
      )
      if (fileIndex !== -1) {
        state.files.splice(fileIndex, 1)
      }
    })
    .addCase(updatePage, (state, action) => {
      const { fileId, page: updatedPage } = action.payload
      const fileIndex = state.files.findIndex((file) => file.id === fileId)

      if (fileIndex !== -1) {
        const pages = state.files[fileIndex].pages
        const pageIndex = pages.findIndex(
          (page) => page.number === updatedPage.number,
        )

        if (pageIndex !== -1) {
          pages[pageIndex] = {
            ...pages[pageIndex],
            ...updatedPage,
          }

          const recognizedPages = state.recognizedPages
          const recognizedPageIndex = recognizedPages.findIndex(
            (page) => page.id === updatedPage.id,
          )
          if (recognizedPageIndex !== -1) {
            state.recognizedPages[recognizedPageIndex] = {
              ...recognizedPages[recognizedPageIndex],
              ...updatedPage,
            }
          }

          let unrecognizedPageIndex = -1
          let fileIndex = -1
          for (let i = 0; i < state.unrecognizedPages.length; i++) {
            unrecognizedPageIndex = state.unrecognizedPages[i].pages.findIndex(
              (page) => page.id === updatedPage.id,
            )

            if (unrecognizedPageIndex !== -1) {
              fileIndex = i
              break
            }
          }

          if (unrecognizedPageIndex !== -1) {
            state.unrecognizedPages[fileIndex].pages[unrecognizedPageIndex] = {
              ...recognizedPages[unrecognizedPageIndex],
              ...updatedPage,
            }
          }
        }
      }
    })
    .addCase(addFileDescription, (state, action) => {
      state.analyzeResult = action.payload.description
    })
    .addCase(sortPages, (state) => {
      for (const file of state.files) {
        const pages = []
        for (const page of file.pages) {
          if (state.analyzeResult) {
            let isRecognized = false
            for (const floorplan of state.analyzeResult.floorplans) {
              if (
                floorplan.file_name === file.s3Filename &&
                floorplan.page_number === page.number
              ) {
                isRecognized = true
                state.recognizedPages.push({
                  ...page,
                  name: floorplan.name,
                  drawingType: DrawingType.Floorplan,
                  isChecked: true,
                })
              }
            }

            for (const elevation of state.analyzeResult.elevations) {
              if (
                elevation.file_name === file.s3Filename &&
                elevation.page_number === page.number
              ) {
                isRecognized = true
                state.recognizedPages.push({
                  ...page,
                  name: elevation.name,
                  drawingType: DrawingType.Elevation,
                  isChecked: true,
                })
              }
            }

            if (!isRecognized) {
              pages.push(page)
            }
          } else {
            pages.push(page)
          }
        }

        state.unrecognizedPages.push({ fileName: file.name, pages })
      }
    })
    .addCase(switchPageCheckbox, (state, action) => {
      const pageIndex = state.recognizedPages.findIndex(
        (page) => page.id === action.payload.pageId,
      )
      if (pageIndex !== -1) {
        state.recognizedPages[pageIndex].isChecked =
          !state.recognizedPages[pageIndex].isChecked
      }
    })
    .addCase(selectPage, (state, action) => {
      state.selectedPageId = action.payload.pageId
    })
    .addCase(replacePage, (state, action) => {
      const pageIndex = state.recognizedPages.findIndex(
        (page) => page.id === action.payload.pageId,
      )
      let newPageIndex = -1
      let fileIndex = -1
      for (let i = 0; i < state.unrecognizedPages.length; i++) {
        newPageIndex = state.unrecognizedPages[i].pages.findIndex(
          (page) => page.id === action.payload.newPageId,
        )

        if (newPageIndex !== -1) {
          fileIndex = i
          break
        }
      }

      if (pageIndex !== -1 && newPageIndex !== -1) {
        const page = state.recognizedPages[pageIndex]
        state.recognizedPages[pageIndex] = {
          ...state.unrecognizedPages[fileIndex].pages[newPageIndex],
          name: page.name,
          drawingType: page.drawingType,
          isChecked: page.isChecked,
        }
        if (
          page.filename ===
          state.unrecognizedPages[fileIndex].pages[newPageIndex].filename
        ) {
          state.unrecognizedPages[fileIndex].pages[newPageIndex] = {
            id: page.id,
            number: page.number,
            image: page.image,
            filename: page.filename,
          }
        } else {
          const anotherFileIndex = state.unrecognizedPages.findIndex(
            (file) => file.fileName === page.filename,
          )
          if (fileIndex !== -1) {
            const filteredByNumber = state.unrecognizedPages[
              fileIndex
            ].pages.filter((p) => p.number < page.number)
            const pageIndex = state.unrecognizedPages[
              fileIndex
            ].pages.findIndex(
              (p) => p === filteredByNumber[filteredByNumber.length - 1],
            )
            state.unrecognizedPages[anotherFileIndex].pages[pageIndex] = {
              id: page.id,
              number: page.number,
              image: page.image,
              filename: page.filename,
            }
          }
        }
      }
    })
    .addCase(addPage, (state, action) => {
      let pageIndex = -1
      let fileIndex = -1
      for (let i = 0; i < state.unrecognizedPages.length; i++) {
        pageIndex = state.unrecognizedPages[i].pages.findIndex(
          (page) => page.id === action.payload.pageId,
        )

        if (pageIndex !== -1) {
          fileIndex = i
          break
        }
      }

      if (fileIndex !== -1 && pageIndex !== -1) {
        const page = state.unrecognizedPages[fileIndex].pages[pageIndex]
        const pageName =
          action.payload.type === DrawingType.Floorplan
            ? `${pageNames[state.recognizedPages.filter((page) => page.drawingType === DrawingType.Floorplan).length]} Floor Plan`
            : 'Building Elevations'
        state.recognizedPages.push({
          ...page,
          drawingType: action.payload.type,
          name: pageName,
          isChecked: true,
        })
        state.unrecognizedPages[fileIndex].pages.splice(pageIndex, 1)
      }
    })
    .addCase(removePage, (state, action) => {
      const pageIndex = state.recognizedPages.findIndex(
        (page) => page.id === action.payload.pageId,
      )

      if (pageIndex !== -1) {
        const page = state.recognizedPages[pageIndex]
        const fileIndex = state.unrecognizedPages.findIndex(
          (file) => file.fileName === page.filename,
        )
        if (fileIndex !== -1) {
          const filteredByNumber = state.unrecognizedPages[
            fileIndex
          ].pages.filter((p) => p.number < page.number)
          const newPageIndex = state.unrecognizedPages[
            fileIndex
          ].pages.findIndex(
            (p) => p === filteredByNumber[filteredByNumber.length - 1],
          )
          state.unrecognizedPages[fileIndex].pages.splice(newPageIndex, 0, {
            id: page.id,
            number: page.number,
            image: page.image,
            filename: page.filename,
          })

          state.recognizedPages.splice(pageIndex, 1)
        }
      }
    })
    .addCase(selectFile, (state, action) => {
      state.selectedFileName = action.payload.fileName
    })
    .addCase(setIsLoading, (state, action) => {
      state.isLoading = action.payload
    })
    .addCase(updateS3FolderName, (state, action) => {
      state.selecetedS3Folder = action.payload
    })
    .addCase(setFilesType, (state, action) => {
      state.filesType = action.payload
    })
    .addCase(setConvertIssues, (state, action) => {
      state.convertResult = { issues: action.payload, fixes: {} }
    })
    .addCase(setConvertFailedError, (state, action) => {
      if (action.payload === undefined) {
        state.convertFailedError = undefined
      } else {
        state.convertFailedError = { type: action.payload }
      }
    })
    .addCase(setConvertFixes, (state, action) => {
      if (!state.convertResult || !state.convertResult.issues) {
        return
      }

      state.convertResult.fixes = { best_splits: action.payload }
    })
    .addCase(clearAnalyzeResults, (state, action) => {
      state.analyzeResult = initialState.analyzeResult
      state.unrecognizedPages = initialState.unrecognizedPages
      state.recognizedPages = initialState.recognizedPages
      state.selectedFileName = initialState.selectedFileName
    })
    .addCase(clearConvertResults, (state, action) => {
      state.convertResult = initialState.convertResult
    })
    .addCase(clearState, () => {
      return initialState
    })
})

export default filesReducer
